/**
 * Glossário central de termos e textos de ajuda da interface.
 *
 * Fonte única dos rótulos "em linguagem simples" e das explicações mostradas em
 * `HelpTip`. Não hardcode esses textos nas telas - importe daqui. Ver o guia em
 * `docs/ux-linguagem-simples.md`.
 */
import type { ProcessoConsultaResponse, Visibilidade } from '@/types/api';
import type { SituacaoMeta } from './metas';
import { truncar } from './texto';

type CacheStatus = ProcessoConsultaResponse['cacheStatus'];

/** Cor do selo de situação. Mapeada para classes Tailwind em `CacheBadge`. */
export type TomCache = 'verde' | 'cinza' | 'ambar' | 'amarelo';

export interface CacheInfo {
  /** Rótulo curto e claro mostrado ao usuário. */
  rotulo: string;
  tom: TomCache;
  /** Explicação mostrada no HelpTip ao lado do selo. */
  ajuda: string;
}

/**
 * "Situação dos dados" (antiga coluna "Cache"). As CHAVES espelham o enum do
 * backend (`ProcessoConsultaResponse.cacheStatus`); o `satisfies` abaixo quebra a
 * compilação se o backend adicionar/renomear um status.
 */
export const CACHE_STATUS = {
  FRESH: {
    rotulo: 'Atualizado agora',
    tom: 'verde',
    ajuda: 'Buscado agora há pouco direto da fonte (eCIGA ou SEI). É o dado mais atual possível.',
  },
  HIT: {
    rotulo: 'Recente',
    tom: 'cinza',
    ajuda:
      'Cópia recente que o sistema guardou para responder mais rápido. Atualiza sozinha em alguns minutos; clique em "Atualizar" para buscar agora.',
  },
  STALE: {
    rotulo: 'Pode estar desatualizado',
    tom: 'ambar',
    ajuda:
      'A fonte (eCIGA ou SEI) não respondeu agora, então mostramos a última cópia guardada. O sistema tentará atualizar sozinho.',
  },
  UNRESOLVED: {
    rotulo: 'Localizando processo',
    tom: 'amarelo',
    ajuda:
      'Este processo ainda não foi localizado na fonte (eCIGA ou SEI). Confira se o número está correto; o sistema tentará de novo na próxima atualização.',
  },
} satisfies Record<CacheStatus, CacheInfo>;

/** Ajuda da coluna inteira "Situação dos dados". */
export const SITUACAO_DADOS_AJUDA =
  'Mostra quão recente é cada informação. O sistema guarda uma cópia para responder rápido e a atualiza de tempos em tempos.';

/** Filtro "Quem pode ver" (antigo "Escopo") na lista de processos. */
export const ESCOPO = {
  rotulo: 'Quem pode ver',
  ajuda:
    'Cada acompanhamento recebe, no cadastro, um nível de quem pode vê-lo. Este filtro mostra só os acompanhamentos de um nível: "Privado" = só de quem cadastrou; "Secretaria" = de toda a secretaria; "Subsecretaria/Coordenadoria" = do seu setor. "Todos" mostra os três níveis juntos. A lista já traz apenas o que você tem permissão de ver.',
  opcoes: {
    TODOS: 'Todos',
    SECRETARIA: 'Secretaria',
    SUBSETOR: 'Subsecretaria/Coordenadoria',
    PRIVADO: 'Privado',
  } satisfies Record<'TODOS' | Visibilidade, string>,
  /** Estado vazio: nenhum processo cadastrado ainda. */
  vazioTodos:
    'Nenhum processo cadastrado ainda. Use o formulário acima ("Adicionar") para incluir o primeiro.',
  /** Estado vazio: há processos, mas nenhum no filtro de "Quem pode ver" ativo. */
  vazioFiltro:
    'Nenhum processo neste filtro de "Quem pode ver". Volte o filtro para "Todos" ou adicione um novo processo.',
  /** Rótulo do campo de visibilidade no cadastro/edição do processo. */
  visibilidadeRotulo: 'Visibilidade',
  /** Ajuda do campo de visibilidade: o que se controla é o acompanhamento, não o processo no eCIGA. */
  visibilidadeAjuda:
    'O processo no eCIGA é público por lei. Aqui você escolhe quem vê SUA meta e anotações neste sistema: só você, sua subsecretaria/coordenadoria, ou toda a sua secretaria.',
  /**
   * Frase "Quem verá seu acompanhamento: ..." mostrada no cadastro e na edição.
   * Recebe a sigla já montada pelo componente (string vazia quando não há sigla).
   */
  quemVera(visibilidade: Visibilidade, sigla: string): string {
    switch (visibilidade) {
      case 'SECRETARIA':
        return `Quem verá seu acompanhamento: toda a secretaria${sigla ? ` (${sigla})` : ''}.`;
      case 'SUBSETOR':
        return `Quem verá seu acompanhamento: sua subsecretaria ou coordenadoria${sigla ? ` (${sigla})` : ''}.`;
      case 'PRIVADO':
        return 'Quem verá seu acompanhamento: somente você.';
    }
  },
};

/** Moldura do sistema (cabeçalho e menu): rótulos falados para quem usa leitor de tela. */
export const LAYOUT = {
  pularParaConteudo: 'Pular para o conteúdo',
  menuPrincipal: 'Menu principal',
};

/** Tela "Processos": o formulário de cadastro e a lista. */
export const PROCESSOS = {
  titulo: 'Processos',
  cadastroTitulo: 'Acompanhar um novo processo',
  adicionar: 'Adicionar',
  listaRotulo: 'Lista de processos acompanhados',
  baixarRotulo: 'Baixar',
  colunaDetalhes: 'Detalhes',
  expandir: 'Expandir detalhes',
  recolher: 'Recolher detalhes',
  sigiloso: 'Sigiloso',
  sigilosoAjuda: (sistema: string) => `Processo com restrição de acesso no ${sistema}`,
  sigilosoAviso: (sistema: string) =>
    `Processo com restrição de acesso no ${sistema}. Trate as informações com sigilo.`,
};

/**
 * Filtro por sistema de origem do processo. "eCIGA" e "SEI" são os nomes dos dois
 * sistemas da Prefeitura — não são jargão de TI, mas nem todo mundo sabe qual é qual
 * pelo número, então a ajuda explica pelo formato.
 */
export const ORIGEM = {
  rotulo: 'Sistema',
  ajuda:
    'Os processos vêm de dois sistemas. O eCIGA usa números como 9900251828/2025; o SEI usa ' +
    'números como NIT-050136/000348/2026. Este filtro mostra só os de um deles. "Todos" ' +
    'mostra os dois juntos.',
  opcoes: {
    TODAS: 'Todos',
    ECIGA: 'eCIGA',
    SEI: 'SEI',
  } satisfies Record<'TODAS' | 'ECIGA' | 'SEI', string>,
  /** Estado vazio: há processos, mas nenhum do sistema escolhido. */
  vazioFiltro: (sistema: string) =>
    `Nenhum processo do ${sistema} na sua lista. Volte o filtro de sistema para "Todos" ` +
    'ou adicione um processo desse sistema.',
};

/** Busca rápida na lista de processos (filtra o que já está carregado). */
export const BUSCA = {
  rotulo: 'Buscar processo',
  placeholder: 'Buscar por número, ID da meta, meta, tipo ou assunto…',
  ajuda:
    'Procura o que você digitar no número, no ID da meta, no nome da meta, no tipo, no assunto e na localização dos processos já carregados. O ID da meta é comparado inteiro: "ION-2E1" acha só a meta ION-2E1 (não a ION-2E10), e "ION" acha todas as metas da ION. Para ver só uma meta, use também o filtro "Meta".',
  limpar: 'Limpar busca',
  semResultado: 'Nenhum processo encontrado para a sua busca. Confira o termo ou limpe a busca.',
};

/**
 * Paginação da lista de processos. "Paginação" é palavra comum, mas o controle de
 * quantos itens aparecem por vez precisa de rótulo explícito — daí "Processos por
 * página" em vez de "itens/página" ou "page size".
 */
export const PAGINACAO = {
  porPaginaRotulo: 'Processos por página',
  porPaginaAjuda:
    'Escolha quantos processos aparecem de cada vez. Listas menores carregam e rolam mais rápido; ' +
    '"Todos" mostra a lista inteira numa página só. A sua escolha fica guardada neste aparelho.',
  todos: 'Todos',
  anterior: 'Página anterior',
  proxima: 'Próxima página',
  irParaPagina: (n: number) => `Ir para a página ${n}`,
  /** Ex.: "Mostrando 1 a 25 de 137 processos". */
  resumo: (de: number, ate: number, total: number) =>
    total === 1
      ? 'Mostrando o único processo da lista.'
      : `Mostrando ${de} a ${ate} de ${total} processos.`,
  /** Ex.: "Página 2 de 6". */
  posicao: (pagina: number, paginas: number) => `Página ${pagina} de ${paginas}`,
  navegacao: 'Navegação entre as páginas da lista de processos',
};

/**
 * Coluna "Follow UP" — botão que copia um resumo pronto do processo (número,
 * assunto e última tramitação) para colar onde quiser (e-mail, Teams, WhatsApp).
 * "Follow UP" é termo em inglês (jargão); por isso o ícone tem um HelpTip que
 * explica, em pt-BR, o que o botão faz.
 */
export const FOLLOWUP = {
  coluna: 'Follow UP',
  ajuda:
    'Copia um resumo do processo (número, assunto e a última tramitação) já formatado, ' +
    'pronto para você colar e enviar onde quiser - e-mail, Teams ou WhatsApp.',
  copiar: 'Copiar resumo para enviar',
  copiado: 'Copiado!',
};

/** Campo "Apelido do acompanhamento" (antigo "Meta") no cadastro/edição/tabela de processos. */
/** Coluna de tempo na lista de processos: idade (desde a abertura) e parado (desde a última mov.). */
export const TEMPO_PROCESSO = {
  coluna: 'Tempo',
  colunaAjuda:
    '"Aberto há": quantos dias desde que o processo foi aberto. "Parado há": quantos dias desde a última movimentação no eCIGA.',
  abertoLabel: 'Aberto há',
  paradoLabel: 'Parado há',
  abertoHa: (d: number) => `aberto há ${d} ${d === 1 ? 'dia' : 'dias'}`,
  paradoHa: (d: number) => `parado há ${d} ${d === 1 ? 'dia' : 'dias'}`,
  dias: (d: number) => `${d} ${d === 1 ? 'dia' : 'dias'}`,
  semData: 'sem data',
};

/**
 * Campo "Número do processo" — serve para eCIGA e SEI (formatos diferentes).
 *
 * O campo pontua sozinho enquanto a pessoa digita (ver `lib/numeroProcesso.ts`), então os
 * textos aqui falam de **números**, não de pontuação: ninguém precisa saber onde entra a
 * barra. Os exemplos são de processos reais dos dois sistemas.
 */
export const NUMERO_PROCESSO = {
  rotulo: 'Número do processo',
  placeholder: 'Ex.: NIT-050136/000348/2026',
  ajudaLabel: 'Que número usar (eCIGA ou SEI)',
  ajuda:
    'Digite só os números - a pontuação aparece sozinha. Serve para os dois sistemas: ' +
    'SEI (ex.: NIT-050136/000348/2026) e eCIGA (ex.: 9900062785/2026). ' +
    'Copiar e colar também funciona, mesmo com espaços a mais.',
  /** Linha embaixo do campo: mostra o que entendemos antes de a pessoa clicar em Adicionar. */
  entendemos: (origem: 'eCIGA' | 'SEI', numero: string) =>
    `Vamos procurar no ${origem} o processo ${numero}.`,
  /** Ainda não bate com nenhum formato: ensina pelo exemplo, sem falar em "inválido". */
  aindaNao: 'Continue digitando. SEI: NIT-050136/000348/2026 · eCIGA: 9900062785/2026.',
  /** Tem 14 números (cara de eCIGA) mas não começa com 99. */
  ecigaSem99: 'No eCIGA o número começa com 99 (ex.: 9900062785/2026). Confira o começo.',
  // Confirmação quando o sistema DEDUZ algo: hoje, a sigla NIT- de quem digitou só os números.
  confirmaTitulo: 'Confirme o número do processo',
  confirmaTexto: (original: string, sugerido: string) =>
    `Você digitou ${original}. Pelo tamanho, é um processo do SEI - e no SEI o número começa com a sigla da cidade. Você quis dizer ${sugerido}?`,
  confirmaSim: (sugerido: string) => `Sim, usar ${sugerido}`,
  confirmaNao: 'Não, vou corrigir',
};

/**
 * Campos "Meta da Gestão" e "ID da meta" (cadastro, edição, lista) e o filtro "Meta" da lista.
 * O ID é a chave que agrupa os processos na página "Metas da gestão" (ver `lib/metas.ts`).
 */
export const META_PROCESSO = {
  rotulo: 'Meta da Gestão (opcional)',
  rotuloCurto: 'Meta da Gestão',
  placeholder: 'Ex.: Lançar e coordenar o PNCV - PMA 2026',
  ajuda:
    'Nome da meta ligada a este processo (opcional). Use o mesmo nome para todos os processos da mesma meta - é ele que aparece na página "Metas da gestão". Deixar em branco não prejudica o acompanhamento.',
  idRotulo: 'ID da meta (opcional)',
  idRotuloCurto: 'ID da meta',
  idPlaceholder: 'Ex.: SEOP-2E1',
  idAjuda:
    'Código da meta, igual para todos os processos da mesma meta - é ele que agrupa os processos na página "Metas da gestão" e no filtro "Meta". No Plano de Metas (PMA 2026) tem o formato ÓRGÃO-número (ex.: SEOP-2E1); alguns setores usam a sigla do órgão (ex.: ION). Maiúsculas e minúsculas não fazem diferença. O mesmo processo pode ser cadastrado uma vez para cada ID.',
  /** Filtro "Meta" na lista de processos. */
  filtroRotulo: 'Meta',
  filtroAjuda:
    'Mostra só os processos de uma meta, pelo ID da meta. A lista traz os IDs que existem nos seus processos, com a quantidade de processos de cada um. "Sem ID da meta" mostra os processos cadastrados sem ID.',
  filtroTodas: 'Todas as metas',
  filtroSemId: 'Sem ID da meta',
  /** Opção do filtro: "ID — título (n)" quando a meta tem um título único; senão "ID (n processos)". */
  filtroOpcao: (id: string, titulo: string | null, n: number) =>
    titulo
      ? `${id} — ${truncar(titulo, 60)} (${n})`
      : `${id} (${n} ${n === 1 ? 'processo' : 'processos'})`,
  /** Opção que veio pela URL mas não existe nos processos carregados. */
  filtroSemProcesso: (id: string) => `${id} (nenhum processo)`,
  limparFiltro: 'Limpar filtro de meta',
  /** Estado vazio: o filtro de meta não deixou nenhum processo. */
  vazioFiltro: (id: string) =>
    `Nenhum processo da meta "${id}" com os filtros atuais. Volte o filtro de meta para "Todas as metas" ou mude os outros filtros.`,
  vazioSemId:
    'Todos os processos da sua lista têm ID da meta. Volte o filtro de meta para "Todas as metas".',
};

/** Cor do selo de situação da meta. Mapeada para classes Tailwind na página de Metas. */
export type TomSituacaoMeta = 'verde' | 'ambar' | 'vermelho' | 'cinza';

export interface SituacaoMetaInfo {
  rotulo: string;
  tom: TomSituacaoMeta;
  /** Explicação do selo; recebe quantos processos estão na pior faixa e o total da meta. */
  ajuda: (n: number, total: number) => string;
}

/**
 * Página "Metas da gestão": os processos agrupados pelo ID da meta, com os tempos parados.
 * As contas vêm de `lib/metas.ts` (as mesmas da aba "Metas" do Excel, feita pelo backend).
 */
export const METAS = {
  navItem: 'Metas da gestão',
  titulo: 'Metas da gestão',
  subtitulo:
    'Cada meta reúne os processos cadastrados com o mesmo ID da meta. Veja quantos estão parados e há quanto tempo, e abra a meta para ver cada processo.',
  ajuda:
    'Esta página agrupa os processos da sua lista pelo ID da meta informado no cadastro. As contas de tempo são as mesmas da lista de processos. Um processo cadastrado em duas metas aparece nas duas.',
  /** Ex.: "179 metas · 293 processos · 7 metas com processo parado há mais de 90 dias". */
  resumo: (metas: number, processos: number, paradas90: number) =>
    `${metas} ${metas === 1 ? 'meta' : 'metas'} · ${processos} ${processos === 1 ? 'processo' : 'processos'} · ` +
    `${paradas90} ${paradas90 === 1 ? 'meta' : 'metas'} com processo parado há mais de 90 dias`,
  resumoAjuda:
    'Processos diferentes: um processo cadastrado em duas metas, ou por duas pessoas, conta uma vez aqui - mas aparece em cada meta em que está.',
  buscaRotulo: 'Buscar meta',
  buscaPlaceholder: 'Buscar meta por ID ou título…',
  buscaLimpar: 'Limpar busca de metas',
  semResultado: 'Nenhuma meta encontrada para a sua busca. Confira o termo ou limpe a busca.',
  vazio:
    'Nenhum processo cadastrado ainda. Cadastre processos na tela Processos, informando o ID da meta, e eles aparecem aqui agrupados por meta.',
  ordemRotulo: 'Ordenar por',
  ordem: {
    ID: 'ID da meta (A-Z)',
    MAIS_PARADA: 'Mais tempo parado primeiro',
    MAIS_PROCESSOS: 'Mais processos primeiro',
  },
  colunas: {
    id: 'ID da meta',
    meta: 'Meta',
    processos: 'Processos',
    parados30: 'Parados há mais de 30 dias',
    parados90: 'Parados há mais de 90 dias',
    maiorParada: 'Parado há mais tempo',
    ultimaMov: 'Movimentação mais recente',
    situacao: 'Situação da meta',
  },
  colunasAjuda: {
    id: 'É o código que você preencheu no cadastro. No Plano de Metas ele identifica a meta (ex.: SEOP-2E1); em setores que usam a sigla do órgão (ex.: ION), cada linha aqui é um órgão.',
    meta: 'Título da meta, quando todos os processos dela foram cadastrados com o mesmo título. Se cada processo tem uma descrição própria, mostramos quantas descrições há.',
    processos:
      'Quantos processos diferentes estão ligados a esta meta. Um processo cadastrado por duas pessoas conta uma vez.',
    parados30:
      'Processos desta meta sem movimentação há mais de 30 dias. Inclui os que passam de 90. Processos ainda sem data não entram na conta.',
    parados90:
      'Processos desta meta sem movimentação há mais de 90 dias. É o melhor sinal de que algo precisa de cobrança.',
    maiorParada: 'O processo desta meta que está há mais tempo sem movimentação.',
    ultimaMov: 'A data de movimentação mais recente entre os processos desta meta.',
    situacao:
      'Resumo da meta pelo pior processo: vermelho se algum está parado há mais de 90 dias; âmbar se algum passa de 30; verde se todos se movimentaram nos últimos 30 dias; cinza se nenhum tem data ainda. Processos sem data não entram na conta dos parados.',
  },
  situacao: {
    EM_MOVIMENTO: {
      rotulo: 'Em andamento',
      tom: 'verde',
      ajuda: () =>
        'Todos os processos desta meta que já têm data de movimentação se movimentaram nos últimos 30 dias. Processos ainda sem data não entram na conta.',
    },
    PARADO_30: {
      rotulo: 'Parado há mais de 30 dias',
      tom: 'ambar',
      ajuda: (n: number, total: number) =>
        `Pelo menos um processo desta meta está sem movimentação há mais de 30 dias: ${n} de ${total}. Abra a linha para ver quais.`,
    },
    PARADO_90: {
      rotulo: 'Parado há mais de 90 dias',
      tom: 'vermelho',
      ajuda: (n: number, total: number) =>
        `Pelo menos um processo desta meta está sem movimentação há mais de 90 dias: ${n} de ${total}. Abra a linha para ver quais.`,
    },
    AGUARDANDO_DADOS: {
      rotulo: 'Sem dados ainda',
      tom: 'cinza',
      ajuda: () =>
        'Nenhum processo desta meta tem data de movimentação - normalmente porque o sistema ainda está localizando o processo na fonte (eCIGA ou SEI). Veja o selo "Localizando processo" na lista.',
    },
  } satisfies Record<SituacaoMeta, SituacaoMetaInfo>,
  /** Selo com a contagem da pior faixa: "Parado há mais de 90 dias (2 de 27)". */
  seloComContagem: (rotulo: string, n: number, total: number) => `${rotulo} (${n} de ${total})`,
  /** Célula "Meta" quando os processos do grupo têm descrições diferentes. */
  descricoesDiferentes: (n: number) => `${n} descrições diferentes`,
  semId: 'Sem ID da meta',
  /** Célula "Meta" quando nenhum processo do grupo tem título. */
  semTitulo: '-',
  semIdAjuda:
    'Processos cadastrados sem ID da meta. Para levá-los a uma meta, edite cada um na lista de processos e preencha o ID.',
  /** Rótulo da última movimentação de um processo, dentro da meta. */
  ultimaMovProcesso: 'Última movimentação',
  /**
   * Linha do processo dentro da meta: o número está em mais de um acompanhamento (outra pessoa,
   * ou a mesma pessoa em dois níveis de visibilidade) - por isso "vezes", não "pessoas".
   */
  cadastradoPor: (n: number) => `cadastrado ${n} vezes`,
  /** Ex.: "132 dias · 9900001234/2025". */
  diasParadoCom: (dias: number, numero: string) =>
    `${dias} ${dias === 1 ? 'dia' : 'dias'} · ${numero}`,
  expandir: (id: string) => `Ver os processos da meta ${id}`,
  recolher: (id: string) => `Recolher os processos da meta ${id}`,
  processosDaMeta: (id: string) => `Processos da meta ${id}`,
  verHistorico: 'Ver histórico',
  verNaLista: 'Ver na lista de processos',
  baixarExcel: 'Baixar Excel',
  baixarExcelAjuda:
    'Baixa a planilha Excel dos processos do seu setor. Ela tem duas abas: "Processos" (uma linha por processo) e "Metas" (este mesmo resumo, uma linha por meta).',
};

/** Linha do tempo de movimentações (histórico do processo — eCIGA ou SEI). */
export const HISTORICO = {
  vazioTitulo: 'Nenhuma movimentação registrada até agora.',
  vazioAjuda:
    'Se o processo foi cadastrado há pouco, os dados podem ainda não ter chegado ao sistema. Tente "Atualizar" dentro de alguns minutos.',
};

/** Texto de escopo no topo do modal de edição do processo. */
export const PROCESSO_EDICAO = {
  escopoNota:
    'Aqui você ajusta só a descrição interna, quem pode ver este acompanhamento e o canal de avisos. O número e os dados do processo vêm do eCIGA e não mudam aqui.',
  tituloHistorico: 'Histórico',
};

/** Onde a pessoa trabalha (lotação) — mostrado no primeiro acesso e no perfil. */
export const LOTACAO = {
  /** Botão que desiste de criar a ficha e volta para a lista de subsecretarias/coordenadorias. */
  escolherExistente: 'Escolher um existente',
  /** Rótulo falado do indicador de passos do primeiro acesso. */
  passosRotulo: 'Passos do primeiro acesso',
  /** Subtítulo do passo de lotação no primeiro acesso. */
  onboardingAjuda:
    'Diga onde você trabalha: secretaria e subsecretaria/coordenadoria. Assim o sistema sabe a que equipe você pertence. Dá para mudar sua lotação no Perfil quando quiser.',
  /** Introdução do passo de subsecretaria/coordenadoria. */
  subsetorIntro:
    'Procure sua subsecretaria ou coordenadoria na lista abaixo. Se não encontrar, crie a ficha dela aqui.',
};

/** Textos da página "Acesso aos dados" (antiga "Tokens de integração"). */
export const ACESSO_DADOS = {
  titulo: 'Acesso aos dados',
  navItem: 'Acesso aos dados',
  subtitulo:
    'Crie uma chave de acesso para usar seus processos no Excel, no Power BI ou em outras planilhas.',
  ajudaChave:
    'A chave de acesso é uma senha especial que você cola no Excel ou no Power BI para ele buscar seus processos automaticamente. Não é a senha que você usa para entrar no sistema - esta serve só para conectar planilhas e ferramentas. Ela aparece uma única vez, na hora de criar - copie e guarde com segurança. Se perder, é só criar outra.',
  ajudaPrefixo:
    'É só o começo da chave, mostrado para você reconhecê-la na lista. Sozinho, ele não dá acesso a nada.',
  criarTitulo: 'Criar nova chave de acesso',
  nomeLabel: 'Nome (ex.: Excel da equipe de obras)',
  nomePlaceholder: 'Para que serve esta chave?',
  gerarBotao: 'Gerar chave',
  criadaTitulo: 'Chave criada - copie agora! Ela não aparece de novo.',
  listaTitulo: 'Suas chaves ativas',
  listaVazia: 'Nenhuma chave ativa.',
  revogarTitulo: 'Revogar chave de acesso',
  revogarConfirm:
    'Revogar esta chave? Quem a usa (planilhas, Excel) vai parar de receber os dados.',
  revogarBotao: 'Revogar',
  revogadaSucesso:
    'Chave revogada. As planilhas e ferramentas que a usavam vão parar de receber os dados.',
};

/** Textos da tela de entrada (login por senha — padrão — ou conta Microsoft). */
export const LOGIN = {
  titulo: 'Acompanhamento Estratégico',
  subtitulo: 'SEXEC/SSTD - Niterói',

  // Modo senha (padrão)
  emailLabel: 'E-mail',
  senhaLabel: 'Senha',
  botaoEntrar: 'Entrar',
  explicacaoSenha:
    'Use o seu e-mail da Prefeitura e a sua senha. No primeiro acesso, você define uma senha nova. Em caso de dúvida, fale com o suporte da SSTD.',
  credenciaisInvalidas: 'E-mail ou senha incorretos. Verifique e tente novamente.',

  // Modo Microsoft / Entra ID (dormente)
  botao: 'Entrar com a conta Microsoft',
  explicacao:
    'Use a mesma conta e senha do seu e-mail da Prefeitura. A senha é digitada na tela da Microsoft - o sistema não vê nem guarda sua senha.',
  erros: {
    microsoft:
      'Não conseguimos confirmar sua conta Microsoft. Tente entrar de novo; se não funcionar, fale com o suporte da SSTD.',
    expirado:
      'A tela de entrada ficou aberta por muito tempo. Clique no botão para tentar de novo.',
  } as Record<string, string>,
  erroGenerico:
    'Não foi possível entrar. Tente de novo; se não funcionar, fale com o suporte da SSTD.',
  aguarde: 'Confirmando sua conta…',
};

/** Textos da troca de senha (onboarding e perfil). */
export const SENHA = {
  cardTitulo: 'Trocar senha',
  primeiroAcessoTitulo: 'Defina sua senha',
  primeiroAcessoAjuda:
    'Este é o seu primeiro acesso. Escolha uma senha só sua (pelo menos 8 caracteres) para continuar.',
  senhaAtual: 'Senha atual',
  novaSenha: 'Nova senha',
  confirmar: 'Confirmar',
  naoConferem: 'Senhas não conferem',
  trocada: 'Senha alterada.',
  verifiqueCampos: 'Verifique os campos e tente novamente.',
  jaSalva: 'Sua senha nova já está valendo. Use-a da próxima vez que entrar.',
  definidaEmOutroLugar:
    'Sua senha já foi definida em outra janela ou aparelho. Use a senha que você escolheu lá.',
  naoSalvaDefinidaEmOutroLugar:
    'Esta senha não foi salva: a sua já tinha sido definida em outra janela ou aparelho. Use a senha que você escolheu lá.',
  botaoSalvar: 'Trocar senha',
  botaoContinuar: 'Continuar',
};

/** Textos da tela "Avisos no Teams" (webhook por usuário). Sem jargão técnico. */
export const AVISOS_TEAMS = {
  navItem: 'Avisos no Teams',
  titulo: 'Avisos no Teams',
  subtitulo:
    'Receba um aviso no Teams quando um processo que você acompanha se movimentar. Você pode cadastrar vários canais e escolher um para cada processo.',
  // "Que você acompanha" (acima) é o processo que a própria pessoa cadastrou — mas quem vê na
  // lista os processos da equipe lia como "todos os que eu enxergo": testava o canal, via "deu
  // certo" e esperava os avisos da equipe. Esta frase diz com todas as letras de quem são.
  quemRecebe:
    'Os avisos saem dos processos que você mesmo cadastrou. Processos cadastrados por um colega avisam nos canais dele.',
  // "webhook/endpoint" são jargão — na tela chamamos de "canal" / "endereço de aviso".
  nomeLabel: 'Nome do canal',
  nomeAjuda:
    'Um apelido para você reconhecer este canal (ex.: "Equipe de Obras", "Gabinete"). Aparece na hora de escolher o canal de um processo.',
  enderecoLabel: 'Endereço de aviso do Teams',
  enderecoAjuda:
    'É um endereço gerado dentro do Teams (pelo Power Automate) que recebe os avisos e os mostra no canal da sua equipe. Cole aqui o endereço que você criou lá.',
  enderecoPlaceholder: 'Cole aqui o endereço gerado no Power Automate',
  trocarPlaceholder: 'Deixe em branco para manter o atual',
  trocarLinkAjuda:
    'Para trocar o endereço, cole um novo aqui. Deixe em branco para manter o atual — por segurança, ele não é mostrado de novo.',
  comoObterTitulo: 'Como criar o endereço (passo a passo no Power Automate)',
  comoObterIntro:
    'O endereço é gerado por um fluxo do Power Automate (a ferramenta de automação que já vem no Teams) que posta os avisos no seu canal ou grupo do Teams. Crie o fluxo assim:',
  comoObterPassos: [
    'No Teams, escolha o canal (ou o grupo) onde os avisos devem aparecer.',
    'Abra o Power Automate desse canal: passe o mouse no nome do canal (ou do grupo), clique nos três pontinhos (⋯) e escolha "Fluxos de Trabalho". (Também dá para abrir pelo site make.powerautomate.com.)',
    'Crie um fluxo com o gatilho "When a Teams webhook request is received" (Quando uma solicitação de webhook do Teams é recebida).',
    'Adicione a ação "Postar cartão em um chat ou canal".',
    'Em "Postar como" (Post as), escolha Flow bot. Em "Postar em" (Post in), escolha Canal — ou Chat de grupo, se preferir um grupo.',
    'Escolha a Equipe e o Canal de destino (ex.: a equipe/canal "Controle de Processos").',
    'No campo "Cartão Adaptável" (Adaptive Card), cole exatamente a expressão abaixo.',
    'Salve o fluxo. No gatilho ("When a Teams webhook request is received"), copie o endereço (link) gerado.',
    'Cole o endereço no campo "Endereço de aviso do Teams" e clique em Salvar.',
  ] as string[],
  comoObterExpressaoLabel: 'Expressão para o campo "Cartão Adaptável":',
  cartaoAjuda:
    'O "cartão" é a mensagem que o Teams mostra: o número do processo, o ID da meta, a meta, onde o processo está agora, quando ele se movimentou e um botão para abrir no eCIGA. A expressão abaixo entrega ao Teams o cartão que o sistema monta automaticamente.',
  comoObterExpressao: "@{triggerBody()?['cardJson']}",
  comoObterExpressaoAjuda:
    'Essa expressão entrega ao Teams o cartão que o sistema envia. Cole-a como está, sem alterar nada.',
  copiar: 'Copiar',
  copiado: 'Copiado!',
  /** Completa o nome falado de cada botão "Copiar" (há mais de um na tela). */
  copiarCartao: 'a expressão do cartão',
  // Os nomes entre colchetes ('idMeta', 'órgão') são o contrato do envio: o backend os fixa em
  // AvisoTeams.corpo() e docs/integracao-teams.md os descreve. Não renomeie só aqui. O órgão
  // também vai como 'orgao' (sem acento, mesmo valor); a tela ensina a grafia com acento.
  dadosExtrasTitulo: 'Opcional: usar o ID da meta e o órgão no seu fluxo',
  dadosExtrasIntro:
    'Além do cartão, cada aviso leva dois dados separados: o ID da meta e a sigla do órgão. Quem monta o fluxo pode usá-los, por exemplo, para marcar no Teams o responsável pelo órgão. Se você só quer receber os avisos, não precisa desta parte.',
  dadosExtras: [
    {
      rotulo: 'ID da meta',
      expressao: "@{triggerBody()?['idMeta']}",
      copiar: 'a expressão do ID da meta',
    },
    {
      rotulo: 'Órgão (a sigla do começo do ID da meta)',
      expressao: "@{triggerBody()?['órgão']}",
      copiar: 'a expressão do órgão',
    },
  ] as { rotulo: string; expressao: string; copiar: string }[],
  dadosExtrasNotas: [
    'Em "ION-3E10", o órgão é "ION". O ID da meta chega em letras maiúsculas.',
    'O órgão só é preenchido quando o ID tem o formato sigla, traço, número, a letra E e outro número. Num ID como "ION" ou "FMS - ION", o órgão chega vazio.',
    'Se o processo não tem ID da meta, os dois chegam vazios.',
    'No aviso de teste, eles chegam como "TESTE-1E1" e "TESTE".',
    'Quem preferir sem acento pode trocar "órgão" por "orgao" na expressão: o valor é o mesmo.',
  ] as string[],
  ligadoLabel: 'Receber avisos neste canal',
  ligadoAjuda:
    'Quando está ligado, este canal recebe um card no Teams a cada movimentação detectada.',
  adicionar: 'Adicionar canal',
  canaisTitulo: 'Seus canais do Teams',
  novoTitulo: 'Cadastrar um canal de aviso',
  editarTitulo: 'Editar canal de aviso',
  salvar: 'Salvar',
  cancelar: 'Cancelar',
  remover: 'Remover',
  removerTitulo: 'Remover canal de aviso',
  /** Confirmação ao remover um canal: explica a consequência. */
  removerConfirm: (nome: string) =>
    `Remover o canal "${nome}"? Os avisos deixam de ser enviados para ele.`,
  editar: 'Editar',
  enviarTeste: 'Enviar aviso de teste',
  tornarPadrao: 'Tornar padrão',
  padraoBadge: 'Padrão',
  padraoAjuda: 'O canal padrão é usado nos processos que não escolheram um canal específico.',
  salvo: 'Canal salvo.',
  removido: 'Canal removido.',
  testeEnviado: 'Aviso de teste enviado! Confira o canal no Teams.',
  testeFalhou: 'Não foi possível enviar o aviso. Confira o endereço e tente de novo.',
  semCanais: 'Você ainda não cadastrou nenhum canal de aviso do Teams.',
  ultimoEnvio: 'Último aviso enviado com sucesso em',
  ultimoErro: 'Último erro ao enviar',
  desligado: '(desligado)',
  // Seleção de canal no cadastro/edição de um processo.
  canalProcessoLabel: 'Avisar no Teams',
  canalProcessoAjuda: 'Escolha para qual canal vai o aviso quando este processo se movimentar.',
  canalPadraoOpcao: 'Usar meu canal padrão',
};

/** Aviso de atualização do app (PWA) e mensagens de conexão. Linguagem simples. */
export const PWA = {
  atualizacaoTitulo: 'Nova versão disponível',
  atualizacaoTexto:
    'Há uma versão mais nova do sistema. Se você estiver preenchendo algo, termine antes — atualizar vai recarregar a página.',
  atualizar: 'Atualizar agora',
  agoraNao: 'Agora não',
  /** Aviso quando o aparelho está sem internet. */
  semConexao: 'Você está sem internet. Alguns dados podem não carregar até a conexão voltar.',
};

/** Tela "Avisos neste aparelho" (notificações push do celular/computador). Sem jargão. */
export const AVISOS_PUSH = {
  titulo: 'Avisos neste aparelho',
  subtitulo:
    'Receba uma notificação neste celular ou computador quando um processo que você acompanha se movimentar.',
  ajuda:
    'Quando está ligado, este aparelho recebe uma notificação assim que um processo seu muda de setor. Funciona só neste aparelho/navegador — ligue em cada aparelho que quiser avisar. Se outra pessoa entrar no sistema neste aparelho, ela passa a receber os avisos dela, e não os seus. Ao clicar em "Sair", os avisos param até alguém entrar de novo. Você pode desligar quando quiser.',
  ativar: 'Ativar avisos neste aparelho',
  desativar: 'Desativar avisos neste aparelho',
  ativando: 'Ativando…',
  ativado: 'Pronto! Este aparelho vai receber os avisos de movimentação.',
  desativado: 'Avisos desligados neste aparelho.',
  enviarTeste: 'Enviar aviso de teste',
  testeEnviado: 'Aviso de teste enviado! A notificação deve aparecer neste aparelho em instantes.',
  indisponivelServidor: 'Os avisos por notificação ainda não foram habilitados pelo administrador.',
  /** Permissão bloqueada pelo usuário no navegador. */
  permissaoNegada:
    'As notificações estão bloqueadas para este site. Libere as notificações nas configurações do navegador e tente de novo.',
  /** Navegador sem suporte a notificações push. */
  naoSuportado:
    'Este navegador não permite avisos. Use o Chrome (no Android ou no computador) ou instale o app no iPhone.',
  /** Dica específica de iPhone (só funciona com o app instalado). */
  iosAjuda:
    'No iPhone, os avisos só funcionam com o app instalado: abra no Safari, toque em Compartilhar e em "Adicionar à Tela de Início".',
  erroAtivar:
    'Não foi possível ativar os avisos. Tente de novo; se não funcionar, fale com o suporte da SSTD.',
  /** Há inscrição no navegador, mas o servidor não confirmou que ela avisa quem está logado. */
  naoConfirmado:
    'Não foi possível confirmar se este aparelho está recebendo os seus avisos. Verifique a internet e tente de novo.',
  tentarDeNovo: 'Tentar de novo',
};

/** Trilha de atividades (auditoria) — rótulos em linguagem simples. */
export const AUDITORIA = {
  titulo: 'Atividade recente',
  ajuda:
    'Registro de quem fez o quê no sistema (entradas, cadastros, alterações). Útil para acompanhar e investigar.',
  quando: 'Quando',
  quem: 'Quem',
  acao: 'Ação',
  origem: 'Origem (IP)',
  anonimo: '(não identificado)',
  anterior: 'Mais recentes',
  proxima: 'Mais antigas',
  vazio: 'Nenhuma atividade registrada ainda.',
  /** Código técnico da ação → frase simples. Códigos sem tradução aparecem como estão. */
  acoes: {
    login_success: 'Entrou no sistema',
    login_failure: 'Tentativa de entrada falhou',
    logout: 'Saiu do sistema',
    password_changed: 'Trocou a própria senha',
    password_reset: 'Resetou a senha de um usuário',
    pat_created: 'Criou uma chave de acesso',
    pat_revoked: 'Revogou uma chave de acesso',
    user_created: 'Cadastrou uma pessoa',
    user_deleted: 'Removeu uma pessoa',
    user_admin_toggled: 'Alterou permissão de administrador',
    user_updated: 'Editou os dados de uma pessoa',
    /** Código antigo, só de nome/e-mail — mantido para o histórico continuar legível. */
    user_email_changed: 'Corrigiu o e-mail de uma pessoa',
    processo_cadastrado_created: 'Cadastrou um processo',
    processo_cadastrado_updated: 'Editou um processo',
    processo_cadastrado_deleted: 'Removeu um processo',
    cache_refresh_forced: 'Forçou atualização dos dados',
    onboarding_completed: 'Concluiu o primeiro acesso',
    profile_updated: 'Atualizou o perfil',
    subsetor_created: 'Criou uma subsecretaria/coordenadoria',
    teams_webhook_saved: 'Salvou o endereço de avisos do Teams',
    teams_webhook_removed: 'Removeu o endereço de avisos do Teams',
    teams_webhook_tested: 'Enviou um aviso de teste no Teams',
  } as Record<string, string>,
};

/** Textos da página de Administração. */
export const ADMIN = {
  areaTecnicaTitulo: 'Área técnica',
  areaTecnicaAjuda:
    'Endereço para a equipe de TI que precisa ligar este sistema a outros programas. Quem não é de TI não precisa usar.',
  areaTecnicaIntro: 'A equipe técnica pode consultar o',
  areaTecnicaLink: 'manual técnico para conectar este sistema a outras ferramentas',
  /** Frase tranquilizadora no box da senha temporária. */
  senhaTempRecuperacao:
    'Se você perder esta senha, dá para gerar outra: clique em "Resetar senha" ao lado da pessoa, na lista abaixo.',
  /** Confirmação ao remover uma pessoa. */
  removerPessoaTitulo: 'Remover pessoa',
  removerPessoa: 'Remover',
  removerPessoaConfirm: (email: string) =>
    `Remover a pessoa ${email}? Esta ação não pode ser desfeita.`,
  /** Edição dos dados de uma pessoa já cadastrada (nome, e-mail, lotação). */
  editarPessoa: {
    acao: 'Editar',
    titulo: 'Editar dados da pessoa',
    ajuda:
      'Corrija o que estiver errado no cadastro — inclusive o e-mail, quando a pessoa não consegue entrar. A senha, as chaves de acesso e o histórico dela continuam os mesmos, e quem estiver com o sistema aberto não é desconectado.',
    nome: 'Nome completo',
    email: 'E-mail de acesso',
    emailAjuda: 'É com este endereço que a pessoa entra no sistema.',
    secretaria: 'Secretaria',
    subsetor: 'Subsecretaria / Coordenadoria',
    subsetorPrimeiro: 'Escolha a secretaria primeiro',
    subsetorVazio: 'Esta secretaria ainda não tem subsetor cadastrado',
    semLotacao: 'ainda não definida',
    salvar: 'Salvar alterações',
    cancelar: 'Cancelar',
    semAlteracao: 'Você não alterou nenhum campo.',
    lotacaoIncompleta: 'Escolha também o subsetor para concluir a lotação.',
    sucesso: (nome: string) => `Dados de ${nome} atualizados.`,
  },
};

/** Ajudas avulsas reutilizáveis. */
export const AJUDA = {
  atualizar:
    'Busca agora a versão mais recente direto do eCIGA ou do SEI, sem usar a cópia guardada.',
  /** Confirmação de que a busca foi feita (recebe a hora já formatada HH:mm). */
  dadosAtualizados: (hora: string) =>
    `Dados buscados na fonte às ${hora}. O que não mudou continua igual.`,
  baixarPlanilha:
    'Baixa todos os processos do seu setor em uma planilha. Escolha Excel para abrir direto no Excel (recomendado); o CSV é um arquivo de texto para quem usa Power BI ou outros sistemas. Cada linha é um processo, com número, tipo, datas, localização e situação dos dados. O Excel tem ainda uma aba "Metas", com uma linha por ID da meta. Atenção: os filtros da lista NÃO alteram o arquivo - ele vem completo, com todos os processos do setor.',
  /** Aviso de conclusão do download (recebe a hora já formatada HH:mm). */
  planilhaPronta: (hora: string) => `Planilha pronta - confira a pasta Downloads (${hora}).`,
  codigoSuporte:
    'Código desta ocorrência. Ao falar com o suporte, informe este código para ajudar a localizar o que aconteceu.',
};
